/**
 * AssignEstimatorSheet — bottom-sheet modal for assigning an estimator to a quote.
 * Extracted from DispatchPage's AssignSheet for reuse.
 */

import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { api } from '../../lib/api';
import { LoadingSpinner } from './LoadingSpinner';
import type { OrgUser } from '../../types/admin';

interface AssignEstimatorSheetProps {
  orgId: string;
  customerName: string;
  onAssign: (userId: string) => Promise<void>;
  onClose: () => void;
}

export function AssignEstimatorSheet({
  orgId,
  customerName,
  onAssign,
  onClose,
}: AssignEstimatorSheetProps) {
  const [users, setUsers] = useState<OrgUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [assigning, setAssigning] = useState<string | null>(null);

  useEffect(() => {
    api.get<{ success: boolean; data: OrgUser[] }>(`/org-admin/${orgId}/users`)
      .then(res => setUsers(res.data))
      .catch(() => {})
      .finally(() => setLoadingUsers(false));
  }, [orgId]);

  const assignable = users.filter(u =>
    u.status === 'active' && (u.role === 'estimator' || u.role === 'admin' || u.role === 'field_lead'),
  );

  async function handlePick(uid: string) {
    setAssigning(uid);
    try {
      await onAssign(uid);
    } catch {
      setAssigning(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end md:items-center md:justify-center">
      <div className="absolute inset-0 bg-midnight/50" onClick={onClose} aria-hidden="true" />
      <div className="relative bg-white rounded-t-2xl px-4 pt-5 pb-8 md:rounded-2xl md:max-w-sm md:w-full md:pb-6 max-h-[70vh] flex flex-col">
        <div className="md:hidden w-10 h-1 bg-stone rounded-full mx-auto mb-5" />
        <div className="flex items-center justify-between mb-4">
          <div className="min-w-0">
            <h2 className="font-display text-h3 text-midnight">Assign Quote Lead</h2>
            <p className="text-[13px] text-slate mt-0.5 truncate">{customerName}</p>
          </div>
          <button type="button" onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-slate hover:text-midnight hover:bg-mist transition-colors shrink-0"
            aria-label="Close"><X size={18} /></button>
        </div>

        {loadingUsers ? (
          <div className="flex justify-center py-8">
            <LoadingSpinner size="md" />
          </div>
        ) : assignable.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-small text-slate">
              No active estimators found in this organization.
            </p>
          </div>
        ) : (
          <ul className="flex flex-col gap-1.5 overflow-y-auto -mx-1 px-1">
            {assignable.map(u => (
              <li key={u.uid}>
                <button
                  type="button"
                  onClick={() => handlePick(u.uid)}
                  disabled={assigning !== null}
                  className="w-full text-left flex items-center gap-3 px-3 py-3 rounded-xl border border-stone
                             hover:border-plum/30 hover:bg-plum/5 active:bg-mist
                             disabled:opacity-50 transition-all duration-150"
                >
                  <div className="w-8 h-8 rounded-full bg-plum/10 flex items-center justify-center shrink-0 text-[13px] font-semibold text-plum">
                    {u.displayName?.charAt(0)?.toUpperCase() || u.email.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-midnight truncate">
                      {u.displayName || u.email}
                    </p>
                    <p className="text-[12px] text-slate truncate">
                      {u.role === 'admin' ? 'Admin' : 'Estimator'}
                      {u.displayName ? ` · ${u.email}` : ''}
                    </p>
                  </div>
                  {assigning === u.uid && (
                    <LoadingSpinner size="sm" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
